import { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { Upload, Check } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { RadioGroup, RadioGroupItem } from "../../../../ui/radio-group";
import { Switch } from "../../../../ui/switch";
import { Label } from "../../../../ui/label";
import { TagInput, type TagInputHandle } from "./TagInput";
import { useToast } from "../../../../../hooks/use-toast";
import { isValidEmail, STATUS_OPTIONS, CustomerStatus } from "../../../../../lib/customersEngageV1";

// EpicEngage fields displayed in mapping table (left column)
const FIELDS = [
  { key: "external_id", label: "External Customer ID", required: false },
  { key: "first_name", label: "First Name", required: false },
  { key: "last_name", label: "Last Name", required: false },
  { key: "name", label: "Full Name", required: false },
  { key: "email", label: "Email", required: true },
  { key: "phone", label: "Phone / Mobile", required: false },
  { key: "business_name", label: "Company Name", required: false },
  { key: "status", label: "Status", required: false },
  { key: "tags", label: "Tags", required: false },
] as const;

type FieldKey = typeof FIELDS[number]["key"];

// Given an EpicEngage field, find the best matching CSV column header
const autoMapField = (field: FieldKey, csvHeaders: string[]): string => {
  const norm = (s: string) => s.toLowerCase().trim().replace(/[_\-\s]+/g, "");
  const headers = csvHeaders.map((h) => ({ raw: h, n: norm(h) }));
  const find = (preds: ((n: string) => boolean)[]) => {
    for (const p of preds) {
      const hit = headers.find((h) => p(h.n));
      if (hit) return hit.raw;
    }
    return "";
  };
  switch (field) {
    case "email":
      return find([
        (n) => n === "email" || n === "primaryemail" || n === "emailaddress" || n === "mail" || n === "workemail",
        (n) => n === "allemails" || n.includes("primaryemail"),
        (n) => n.includes("email") || n.includes("mail"),
      ]);
    case "phone":
      return find([
        (n) => n === "phone" || n === "mobile" || n === "tel" || n === "telephone" || n === "phonenumber" || n === "mobilephone" || n === "workphone" || n === "primaryphone",
        (n) => n.includes("phone") || n.includes("mobile") || n.includes("tel"),
      ]);
    case "business_name":
      return find([
        (n) => n === "company" || n === "companyname" || n === "business" || n === "businessname" || n === "organization" || n === "organisation" || n === "account" || n === "accountname" || n === "employer",
        (n) => n.includes("company") || n.includes("business") || n.includes("organi") || n.includes("account") || n.includes("employer"),
      ]);
    case "first_name":
      return find([(n) => n === "firstname" || n === "fname" || n === "givenname", (n) => n.includes("first") && n.includes("name")]);
    case "last_name":
      return find([(n) => n === "lastname" || n === "lname" || n === "surname" || n === "familyname", (n) => n.includes("last") && n.includes("name")]);
    case "name":
      return find([(n) => n === "name" || n === "fullname" || n === "customername" || n === "contactname", (n) => n.includes("fullname")]);
    case "external_id":
      return find([(n) => n === "id" || n === "customerid" || n === "externalid" || n === "externalcustomerid" || n === "userid" || n === "contactid" || n === "apolloid", (n) => n.includes("externalid") || n.includes("customerid") || n.includes("contactid")]);
    case "status":
      return find([(n) => n === "status" || n === "state" || n === "stage" || n === "lifecyclestage", (n) => n.includes("status") || n.includes("stage")]);
    case "tags":
      return find([(n) => n === "tag" || n === "tags" || n === "labels" || n === "category" || n === "lists", (n) => n.includes("tag") || n.includes("label") || n.includes("list")]);
    default:
      return "";
  }
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  tagSuggestions: string[];
  onImported: () => void;
};

type Step = 1 | 2 | 3 | 4;

type ParsedRow = Record<string, string>;

type MappedRow = {
  email: string;
  external_id?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  business_name?: string;
  status?: CustomerStatus;
  tags?: string[];
};

// mapping: EpicEngage field key -> CSV column header (or "" if skipped)
type Mapping = Record<FieldKey, string>;

export const ImportCsvDialog = ({ open, onOpenChange, merchantId, tagSuggestions, onImported }: Props) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkTagsRef = useRef<TagInputHandle>(null);

  const [step, setStep] = useState<Step>(1);
  const [filename, setFilename] = useState("");
  const [rawPreview, setRawPreview] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string>("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [mapping, setMapping] = useState<Mapping>({} as Mapping);
  const [bulkTags, setBulkTags] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [reviewStats, setReviewStats] = useState<{ valid: MappedRow[]; invalid: MappedRow[]; skipped: number; existing: number } | null>(null);
  const [duplicateMode, setDuplicateMode] = useState<"update" | "skip">("update");
  const [validOnly, setValidOnly] = useState(true);

  const reset = () => {
    setStep(1); setFilename(""); setHeaders([]); setRows([]); setMapping({} as Mapping);
    setBulkTags([]); setReviewStats(null); setRawPreview([]); setFileError("");
  };

  // Find the first row that has a non-empty value for the given header
  const firstSampleFor = (header: string): string => {
    if (!header) return "";
    for (const r of rows) {
      const v = (r?.[header] ?? "").toString().trim();
      if (v) return v;
    }
    return "";
  };

  const finalizeParse = (name: string, fields: string[], dataRows: ParsedRow[]) => {
    setFilename(name);
    setHeaders(fields);
    setRows(dataRows);
    const initial: Mapping = {} as Mapping;
    for (const f of FIELDS) initial[f.key] = autoMapField(f.key, fields);
    setMapping(initial);
    setStep(2);
  };

  const handleFile = async (file: File) => {
    setFileError("");
    const nameLower = file.name.toLowerCase();
    const ext = nameLower.slice(nameLower.lastIndexOf("."));
    const allowed = [".csv", ".tsv", ".txt"];
    if (!allowed.includes(ext)) {
      const msg = "Please upload a CSV file. Markdown and other file types are not supported.";
      setFileError(msg);
      toast({ title: "Invalid file type", description: msg, variant: "destructive" });
      return;
    }
    try {
      const text = await file.text();
      // Capture raw preview (first 2 non-empty lines, truncated)
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0).slice(0, 2);
      setRawPreview(lines.map((l) => (l.length > 100 ? l.slice(0, 100) + "…" : l)));
      setFilename(file.name);
      // Auto-detect delimiter from first non-empty line
      const firstLine = lines[0] ?? "";
      const counts: Record<string, number> = {
        ",": (firstLine.match(/,/g) ?? []).length,
        ";": (firstLine.match(/;/g) ?? []).length,
        "\t": (firstLine.match(/\t/g) ?? []).length,
        "|": (firstLine.match(/\|/g) ?? []).length,
      };
      const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      const delimiter = best && best[1] > 0 ? best[0] : ",";
      const result = Papa.parse<ParsedRow>(text, {
        header: true,
        skipEmptyLines: true,
        delimiter,
        transformHeader: (h) => h.trim().replace(/^["']|["']$/g, ""),
      });
      const fields = (result.meta.fields ?? []).filter((f) => f && f.length > 0);
      finalizeParse(file.name, fields, result.data as ParsedRow[]);
    } catch (err: any) {
      toast({ title: "Could not read CSV", description: err.message ?? "Unknown error", variant: "destructive" });
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const setFieldMapping = (field: FieldKey, csvHeader: string) => {
    setMapping((prev) => ({ ...prev, [field]: csvHeader }));
  };

  const mappedSample = useMemo(() => {
    return rows.slice(0, 5).map((r) => mapRow(r, mapping));
  }, [rows, mapping]);

  const emailMapped = !!mapping.email;
  const hasIdentifier = !!(mapping.email || mapping.phone || mapping.external_id);

  const goToReview = async () => {
    setAdvancing(true);
    try {
      const all = rows.map((r) => mapRow(r, mapping));
      const valid: MappedRow[] = [];
      const invalid: MappedRow[] = [];
      let skipped = 0;
      for (const r of all) {
        if (!r.email || !isValidEmail(r.email)) {
          skipped++;
          invalid.push(r);
          continue;
        }
        valid.push({ ...r, email: r.email.toLowerCase() });
      }
      // Dedupe by email within file (keep last)
      const map = new Map<string, MappedRow>();
      for (const r of valid) map.set(r.email, r);
      const deduped = Array.from(map.values());

      // Chunk the existence check — large `.in()` queries can exceed URL limits and stall.
      const emails = deduped.map((r) => r.email);
      const existingSet = new Set<string>();
      const CHUNK = 300;
      for (let i = 0; i < emails.length; i += CHUNK) {
        const slice = emails.slice(i, i + CHUNK);
        const { data, error } = await epicengageSupabasev1
          .from("customers")
          .select("email")
          .eq("merchant_id", merchantId)
          .in("email", slice);
        if (error) throw error;
        for (const row of data ?? []) existingSet.add(row.email);
      }

      setReviewStats({ valid: deduped, invalid, skipped, existing: existingSet.size });
      setStep(4);
    } catch (err: any) {
      console.error("goToReview failed", err);
      toast({ title: "Could not prepare review", description: err.message ?? "Try again", variant: "destructive" });
    } finally {
      setAdvancing(false);
    }
  };

  const runImport = async () => {
    if (!reviewStats) return;
    setImporting(true);
    try {
      const tagsForAll = bulkTags
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      const chunkSize = 200;
      const allCustomers: { id: string; email: string }[] = [];

      // Determine which rows to upsert based on duplicate handling
      let rowsToImport = reviewStats.valid;
      if (duplicateMode === "skip" && reviewStats.existing > 0) {
        const { data: existingRows } = await epicengageSupabasev1
          .from("customers")
          .select("email")
          .eq("merchant_id", merchantId)
          .in("email", reviewStats.valid.map((r) => r.email));
        const existingSet = new Set((existingRows ?? []).map((r) => r.email));
        rowsToImport = reviewStats.valid.filter((r) => !existingSet.has(r.email));
      }

      for (let i = 0; i < rowsToImport.length; i += chunkSize) {
        const chunk = rowsToImport.slice(i, i + chunkSize).map((r) => ({
          merchant_id: merchantId,
          email: r.email,
          name: r.name ?? null,
          phone: r.phone ?? null,
          business_name: r.business_name ?? null,
          external_id: r.external_id ?? null,
          status: r.status ?? "new",
          source: "csv",
          archived: false,
          archived_at: null,
        }));
        const { data, error } = await epicengageSupabasev1
          .from("customers")
          .upsert(chunk, { onConflict: "merchant_id,email", ignoreDuplicates: duplicateMode === "skip" })
          .select("id, email");
        if (error) throw error;
        if (data) allCustomers.push(...data);
      }

      // Make sure we have an id for every valid email — including existing customers
      // that the upsert didn't return (e.g. when ignoreDuplicates=true in skip mode).
      const knownEmails = new Set(allCustomers.map((c) => c.email));
      const missingEmails = reviewStats.valid
        .map((r) => r.email)
        .filter((e) => !knownEmails.has(e));
      if (missingEmails.length > 0) {
        for (let i = 0; i < missingEmails.length; i += 500) {
          const chunk = missingEmails.slice(i, i + 500);
          const { data: lookup, error: lookupErr } = await epicengageSupabasev1
            .from("customers")
            .select("id, email")
            .eq("merchant_id", merchantId)
            .in("email", chunk);
          if (lookupErr) throw lookupErr;
          if (lookup) allCustomers.push(...lookup);
        }
      }

      // Build tag rows: per-row tags + bulk tags applied to every imported customer
      const tagRows: { merchant_id: string; customer_id: string; tag: string }[] = [];
      const idByEmail = new Map(allCustomers.map((c) => [c.email, c.id]));
      for (const r of reviewStats.valid) {
        const cid = idByEmail.get(r.email);
        if (!cid) continue;
        const rowTags = (r.tags ?? []).map((t) => t.trim().toLowerCase()).filter(Boolean);
        const allTags = new Set<string>([...rowTags, ...tagsForAll]);
        for (const tag of allTags) {
          tagRows.push({ merchant_id: merchantId, customer_id: cid, tag });
        }
      }

      for (let i = 0; i < tagRows.length; i += 500) {
        const chunk = tagRows.slice(i, i + 500);
        const { error: tagErr } = await epicengageSupabasev1
          .from("customer_tags")
          .upsert(chunk, { onConflict: "customer_id,tag", ignoreDuplicates: true });
        if (tagErr) {
          console.error("customer_tags upsert failed", tagErr);
          throw tagErr;
        }
      }

      toast({
        title: "Import complete",
        description: `${rowsToImport.length} customers imported${duplicateMode === "update" ? " or restored" : ""}${tagsForAll.length ? ` with tags: ${tagsForAll.join(", ")}` : ""}.`,
      });
      reset();
      onOpenChange(false);
      onImported();
    } catch (err: any) {
      console.error("CSV import failed", err);
      toast({ title: "Import failed", description: err.message ?? "Try again", variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import customers from CSV</DialogTitle>
          <DialogDescription>Step {step} of 4</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:bg-accent/30 transition-colors"
            >
              <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Drop your CSV here or click to upload</p>
              <p className="text-xs text-muted-foreground mt-1">Accepted: .csv, .tsv, .txt — first row should contain column headers</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
              />
            </div>
            {fileError && (
              <p className="text-sm text-destructive">{fileError}</p>
            )}
            {filename && rawPreview.length > 0 && !fileError && (
              <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1.5">
                <p className="text-xs font-medium text-foreground">{filename}</p>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">First {rawPreview.length} row{rawPreview.length === 1 ? "" : "s"} preview</p>
                <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all">{rawPreview.join("\n")}</pre>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{filename}</span> — {rows.length} rows, {headers.length} columns detected. Map each EpicEngage field to a CSV column.
            </p>
            <div className="rounded-md border border-border overflow-hidden">
              <div className="grid grid-cols-[1.2fr_1.4fr_1.4fr] gap-3 px-3 py-2 bg-muted text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <div>EpicEngage Field</div>
                <div>CSV Column</div>
                <div>Preview</div>
              </div>
              <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                {(() => {
                  // Dedupe headers and drop blanks so every row's dropdown is identical and non-empty.
                  const cleanHeaders = Array.from(new Set(headers.filter((h) => h && h.trim().length > 0)));
                  return FIELDS.map((f) => {
                    const selected = mapping[f.key] ?? "";
                    const sample = selected ? firstSampleFor(selected) : "";
                    const isEmailMissing = f.key === "email" && !selected;
                    // If the auto-mapped value isn't in the deduped list (edge case), include it so the trigger renders correctly.
                    const optionList = selected && !cleanHeaders.includes(selected)
                      ? [selected, ...cleanHeaders]
                      : cleanHeaders;
                    return (
                      <div key={f.key} className="grid grid-cols-[1.2fr_1.4fr_1.4fr] items-center gap-3 px-3 py-2.5">
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          {f.label}
                          {f.required && <span className="text-destructive">*</span>}
                        </div>
                        <Select
                          value={selected || "__skip__"}
                          onValueChange={(v) => setFieldMapping(f.key, v === "__skip__" ? "" : v)}
                        >
                          <SelectTrigger className={`h-9 ${isEmailMissing ? "border-destructive" : ""}`}>
                            <SelectValue placeholder="Skip this field" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip__">Skip this field</SelectItem>
                            {optionList.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="text-xs text-muted-foreground truncate" title={sample}>
                          {selected ? (sample || <span className="italic">empty</span>) : <span className="italic">—</span>}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            {!emailMapped && (
              <p className="text-sm text-destructive">Email is required — please map a CSV column to Email to continue.</p>
            )}
            {emailMapped && !hasIdentifier && (
              <p className="text-sm text-destructive">Map at least one of Email, Phone, or External Customer ID.</p>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button disabled={!emailMapped || !hasIdentifier} onClick={() => setStep(3)}>Next</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Preview of first 5 rows. Optionally apply a tag to every imported customer.</p>
            {(() => {
              // Only show columns the user actually mapped. Treat first_name/last_name as
              // contributing to the synthesized "Name" column.
              const nameMapped = !!(mapping.name || mapping.first_name || mapping.last_name);
              const previewCols: { key: keyof MappedRow | "name"; label: string; render: (r: MappedRow) => string }[] = [];
              if (nameMapped) previewCols.push({ key: "name", label: "Name", render: (r) => r.name ?? "" });
              if (mapping.email) previewCols.push({ key: "email", label: "Email", render: (r) => r.email ?? "" });
              if (mapping.phone) previewCols.push({ key: "phone", label: "Phone", render: (r) => r.phone ?? "" });
              if (mapping.business_name) previewCols.push({ key: "business_name", label: "Company Name", render: (r) => r.business_name ?? "" });
              if (mapping.external_id) previewCols.push({ key: "external_id", label: "External Customer ID", render: (r) => r.external_id ?? "" });
              if (mapping.status) previewCols.push({ key: "status", label: "Status", render: (r) => r.status ?? "" });
              if (mapping.tags) previewCols.push({ key: "tags", label: "Tags", render: (r) => (r.tags ?? []).join(", ") });
              return (
                <div className="rounded-md border border-border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        {previewCols.map((c) => (
                          <th key={c.key as string} className="text-left px-3 py-2 font-medium">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mappedSample.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          {previewCols.map((c) => {
                            const v = c.render(r);
                            return <td key={c.key as string} className="px-3 py-2">{v || "—"}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Apply tag(s) to all imported customers</label>
              <TagInput ref={bulkTagsRef} value={bulkTags} onChange={setBulkTags} suggestions={tagSuggestions} placeholder="Type a tag, press Enter — add as many as you need" />
              <p className="text-xs text-muted-foreground">Every imported customer will receive all tags shown above. Suggestions come from your existing tags.</p>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} disabled={advancing}>Back</Button>
              <Button
                disabled={advancing}
                onClick={() => {
                  // Commit any text still in the tag input before moving on.
                  bulkTagsRef.current?.flush();
                  goToReview();
                }}
              >
                {advancing ? "Preparing…" : "Continue"}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && reviewStats && (() => {
          // Build preview columns: Email first, Name second, then remaining mapped fields.
          const nameMapped = !!(mapping.name || mapping.first_name || mapping.last_name);
          type Col = { key: string; label: string; render: (r: MappedRow) => string };
          const cols: Col[] = [
            { key: "email", label: "Email", render: (r) => r.email ?? "" },
          ];
          if (nameMapped) cols.push({ key: "name", label: "Name", render: (r) => r.name ?? "" });
          if (mapping.phone) cols.push({ key: "phone", label: "Phone", render: (r) => r.phone ?? "" });
          if (mapping.business_name) cols.push({ key: "business_name", label: "Company Name", render: (r) => r.business_name ?? "" });
          if (mapping.external_id) cols.push({ key: "external_id", label: "External Customer ID", render: (r) => r.external_id ?? "" });
          if (mapping.status) cols.push({ key: "status", label: "Status", render: (r) => r.status ?? "" });
          if (mapping.tags) cols.push({ key: "tags", label: "Tags", render: (r) => (r.tags ?? []).join(", ") });

          const totalRows = reviewStats.valid.length + reviewStats.skipped;
          const mappedFieldsCount = Object.values(mapping).filter((v) => !!v).length;
          const enrichmentsCount = bulkTags.length;
          const previewSource = validOnly ? reviewStats.valid : [...reviewStats.valid, ...reviewStats.invalid];
          const previewRows = previewSource.slice(0, 5);
          const importCount = duplicateMode === "skip"
            ? Math.max(0, reviewStats.valid.length - reviewStats.existing)
            : reviewStats.valid.length;

          return (
            <div className="space-y-5">
              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-border p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Rows</p>
                  <p className="text-2xl font-semibold mt-1">{totalRows}</p>
                </div>
                <div className="rounded-md border border-border p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Mapped Fields</p>
                  <p className="text-2xl font-semibold mt-1">{mappedFieldsCount}</p>
                </div>
                <div className="rounded-md border border-border p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Enrichments</p>
                  <p className="text-2xl font-semibold mt-1">{enrichmentsCount}</p>
                </div>
              </div>

              {/* Duplicate handling */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">If a customer already exists</Label>
                <RadioGroup value={duplicateMode} onValueChange={(v) => setDuplicateMode(v as "update" | "skip")} className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="update" id="dup-update" />
                    <Label htmlFor="dup-update" className="text-sm font-normal cursor-pointer">Update existing</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="skip" id="dup-skip" />
                    <Label htmlFor="dup-skip" className="text-sm font-normal cursor-pointer">Skip duplicates</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Valid only toggle */}
              <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
                <div>
                  <Label htmlFor="valid-only" className="text-sm font-medium cursor-pointer">Import valid rows only</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {reviewStats.skipped > 0
                      ? `${reviewStats.skipped} row${reviewStats.skipped === 1 ? "" : "s"} skipped (missing or invalid email)`
                      : "All rows have a valid email"}
                  </p>
                </div>
                <Switch id="valid-only" checked={validOnly} onCheckedChange={setValidOnly} />
              </div>

              {/* Preview table */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Preview (first {previewRows.length})</p>
                <div className="rounded-md border border-border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        {cols.map((c) => (
                          <th key={c.key} className="text-left px-3 py-2 font-medium">{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          {cols.map((c) => {
                            const v = c.render(r);
                            return <td key={c.key} className="px-3 py-2">{v && v.trim() ? v : "—"}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(3)} disabled={importing}>Back</Button>
                <Button onClick={runImport} disabled={importing || importCount === 0}>
                  {importing ? "Importing…" : (<><Check className="h-4 w-4" /> Import {importCount} Customer{importCount === 1 ? "" : "s"}</>)}
                </Button>
              </div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};

function mapRow(row: ParsedRow, mapping: Mapping): MappedRow {
  const out: MappedRow = { email: "" };
  for (const f of FIELDS) {
    const csvCol = mapping[f.key];
    if (!csvCol) continue;
    const raw = (row[csvCol] ?? "").toString().trim();
    if (!raw) continue;
    if (f.key === "tags") {
      out.tags = raw.split(/[,;|]/).map((t) => t.trim().toLowerCase()).filter(Boolean);
    } else if (f.key === "status") {
      const s = raw.toLowerCase();
      if ((STATUS_OPTIONS as string[]).includes(s)) out.status = s as CustomerStatus;
    } else if (f.key === "email") {
      out.email = raw.toLowerCase();
    } else {
      (out as any)[f.key] = raw;
    }
  }
  // Compose full name from first + last if name not directly mapped
  if (!out.name && (out.first_name || out.last_name)) {
    out.name = [out.first_name, out.last_name].filter(Boolean).join(" ");
  }
  return out;
}
